import { type ReactNode, useState } from "react";

import { StepperProgressBar } from "./StepperProgressBar";
import { StepperContext } from "./useStepper";

export interface StepperProps {
  children: ReactNode;
  className?: string;
  initialStep?: number;
  totalSteps: number;
  withProgressBar?: boolean;
}

export function Stepper({
  children,
  className = "relative w-full h-full",
  initialStep = 1,
  totalSteps,
  withProgressBar = false,
}: StepperProps) {
  const [step, setStep] = useState(initialStep);
  const [activeStep, setActiveStep] = useState(initialStep);
  const [direction, setDirection] = useState(1);
  const [isExiting, setIsExiting] = useState(false);

  const nextStep = () => {
    if (isExiting || step >= totalSteps) return;
    setDirection(1);
    setIsExiting(true);
    setStep((s) => Math.min(s + 1, totalSteps));
  };

  const prevStep = () => {
    if (isExiting || step <= 1) return;
    setDirection(-1);
    setIsExiting(true);
    setStep((s) => Math.max(s - 1, 1));
  };

  const goToStep = (targetStep: number) => {
    if (
      isExiting ||
      targetStep < 1 ||
      targetStep > totalSteps ||
      targetStep === step
    ) {
      return;
    }

    setDirection(targetStep > step ? 1 : -1);
    setIsExiting(true);
    setStep(targetStep);
  };

  const handleAnimationComplete = (definition: string) => {
    if (definition === "exit" && isExiting) {
      setActiveStep(step);
      setIsExiting(false);
    }
  };

  return (
    <StepperContext.Provider
      value={{
        activeStep,
        direction,
        goToStep,
        handleAnimationComplete,
        isExiting,
        nextStep,
        prevStep,
        setStep,
        step,
        totalSteps,
      }}
    >
      <div className={className}>
        {withProgressBar && (
          <div className="mb-6 mt-9 md:mt-0 md:mb-8">
            <StepperProgressBar />
          </div>
        )}
        {children}
      </div>
    </StepperContext.Provider>
  );
}
